import { useCallback, useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import useTableSort from "../hooks/useTableSort";
import {
  getSuperAdminMaintenanceByBusiness,
  getSuperAdminMaintenanceBySalon,
  getSuperAdminMaintenanceLogs,
  getSuperAdminMaintenanceStaff,
  getSuperAdminMaintenanceSummary,
  getSuperAdminMaintenanceTasks,
  getSuperAdminMaintenanceTrends,
} from "../api/superadminReports";
import { SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS } from "../constants/reportTooltips";
import { BTN_OUTLINE, BTN_PRIMARY, FIELD_CLASS, LABEL_TEXT, MODAL_TEXT } from "../ui/modalStyles";

const HELP_GUIDE = {
  title: "Help Guide: Maintenance & Cleaning Analytics (Super Admin)",
  subtitle:
    "Review expected maintenance work, completion quality, and overdue trends across selected scope.",
  sections: [
    {
      id: "scope",
      title: "Scope and Filters",
      description: "Run this report for all businesses, one business, or one salon.",
      bullets: [
        "Business controls overall scope.",
        "Salon options depend on selected business.",
        "Businesses with no salons are still included when business-only data exists.",
      ],
    },
    {
      id: "definitions",
      title: "Definitions",
      description: "Use consistent metric definitions while comparing scope.",
      bullets: [
        "Expected: scheduled occurrences generated from recurrence rules.",
        "On-time: completed at or before due timestamp.",
        "Late: completed after due timestamp.",
        "Missed: expected occurrence with no completion in window.",
      ],
    },
    {
      id: "timezone",
      title: "Timezone Rules",
      description: "Bucketing and status logic use scope-aware timezone rules.",
      bullets: [
        "Salon records use salon timezone.",
        "Business-only records use business timezone.",
        "UTC fallback is used only when business timezone is missing.",
      ],
    },
  ],
};

const DEFAULT_FILTERS = {
  businesses: [],
  salon_options: [],
  staff_options: [],
  category_options: [],
  task_options: [],
  frequency_options: [],
  status_options: [{ value: "ALL", label: "All" }],
  salon_filter_disabled: true,
  business_has_no_salons: false,
  no_salons_note: null,
};

const EMPTY_LIST = [];
const EMPTY_OBJECT = {};

const formatDateISO = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const toNumberText = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return digits ? (0).toFixed(digits) : "0";
  return num.toFixed(digits);
};

const toPercentText = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)}%`;
};

const scopeLabel = (idValue, nameValue) => {
  if (idValue == null && !nameValue) return "--";
  if (idValue == null) return String(nameValue || "--");
  if (!nameValue) return String(idValue);
  return `${idValue} - ${nameValue}`;
};

const csvEscape = (value) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export default function SuperAdminMaintenanceCleaningAnalyticsReport() {
  const { is_super_admin, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight } = useHelpGuideNavigation();

  const today = useMemo(() => formatDateISO(new Date()), []);
  const defaultStart = useMemo(() => formatDateISO(addDays(new Date(), -29)), []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [category, setCategory] = useState("");
  const [frequencyType, setFrequencyType] = useState("");
  const [status, setStatus] = useState("ALL");
  const [taskId, setTaskId] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [businessSearch, setBusinessSearch] = useState("");
  const [salonSearch, setSalonSearch] = useState("");
  const [manualBusinessId, setManualBusinessId] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [refreshKey, setRefreshKey] = useState(0);

  const [appliedParams, setAppliedParams] = useState({
    startDate: defaultStart,
    endDate: today,
    status: "ALL",
    includeInactive: false,
  });

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [taskRows, setTaskRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [byBusinessRows, setByBusinessRows] = useState([]);
  const [bySalonRows, setBySalonRows] = useState([]);
  const [logsRows, setLogsRows] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);

  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [error, setError] = useState("");
  const [filtersError, setFiltersError] = useState("");
  const [logsOpen, setLogsOpen] = useState(true);

  const dateError = useMemo(() => {
    if (!startDate || !endDate) return "Start and end dates are required.";
    if (endDate < startDate) return "End date cannot be before start date.";
    return "";
  }, [startDate, endDate]);

  const bucket = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const dayDiff = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return dayDiff > 60 ? "week" : "day";
  }, [startDate, endDate]);

  const statusOptions = filters.status_options ?? EMPTY_LIST;
  const businesses = filters.businesses ?? EMPTY_LIST;
  const salons = filters.salon_options ?? EMPTY_LIST;
  const staffOptions = filters.staff_options ?? EMPTY_LIST;
  const taskOptions = filters.task_options ?? EMPTY_LIST;
  const categoryOptions = filters.category_options ?? EMPTY_LIST;
  const frequencyOptions = filters.frequency_options ?? EMPTY_LIST;

  const filteredBusinesses = useMemo(() => {
    const q = String(businessSearch || "").trim().toLowerCase();
    if (!q) return businesses;
    return businesses.filter((row) => {
      const label = `${row.id || ""} ${row.code || ""} ${row.name || ""}`.toLowerCase();
      return label.includes(q);
    });
  }, [businessSearch, businesses]);

  const filteredSalons = useMemo(() => {
    const q = String(salonSearch || "").trim().toLowerCase();
    if (!q) return salons;
    return salons.filter((row) => `${row.id || ""} ${row.name || ""}`.toLowerCase().includes(q));
  }, [salonSearch, salons]);

  const loadScopeFilters = useCallback(
    async (nextBusinessId = businessId) => {
      if (dateError) return;
      setLoadingFilters(true);
      setFiltersError("");
      try {
        const payload = await getSuperAdminMaintenanceSummary({
          startDate,
          endDate,
          businessId: nextBusinessId || undefined,
          status: "ALL",
          includeInactive,
        });
        const next = payload?.filters || DEFAULT_FILTERS;
        setFilters({
          ...DEFAULT_FILTERS,
          ...next,
          status_options: (next.status_options || []).length
            ? next.status_options
            : DEFAULT_FILTERS.status_options,
        });
      } catch (err) {
        setFiltersError(err?.message || "Unable to load business/salon filters.");
      } finally {
        setLoadingFilters(false);
      }
    },
    [businessId, dateError, endDate, includeInactive, startDate]
  );

  useEffect(() => {
    loadScopeFilters("");
  }, [loadScopeFilters]);

  const buildDraftParams = () => {
    const params = { startDate, endDate, status, includeInactive };
    if (businessId) params.businessId = Number(businessId);
    if (salonId) params.salonId = Number(salonId);
    if (staffId) params.staffId = Number(staffId);
    if (category) params.category = category;
    if (frequencyType) params.frequencyType = frequencyType;
    if (taskId) params.taskId = Number(taskId);
    return params;
  };

  const runReport = () => {
    if (dateError) {
      setError(dateError);
      return;
    }
    setError("");
    setPage(1);
    setAppliedParams(buildDraftParams());
    setRefreshKey((prev) => prev + 1);
  };

  const loadMain = useCallback(async () => {
    setLoadingMain(true);
    try {
      const [summaryRes, trendsRes, tasksRes, staffRes, byBusinessRes, bySalonRes] = await Promise.all([
        getSuperAdminMaintenanceSummary({ ...appliedParams, bucket }),
        getSuperAdminMaintenanceTrends({ ...appliedParams, bucket }),
        getSuperAdminMaintenanceTasks(appliedParams),
        getSuperAdminMaintenanceStaff(appliedParams),
        getSuperAdminMaintenanceByBusiness(appliedParams),
        getSuperAdminMaintenanceBySalon(appliedParams),
      ]);
      setSummary(summaryRes || null);
      setTrends(Array.isArray(trendsRes?.rows) ? trendsRes.rows : []);
      setTaskRows(Array.isArray(tasksRes?.rows) ? tasksRes.rows : []);
      setStaffRows(Array.isArray(staffRes?.rows) ? staffRes.rows : []);
      setByBusinessRows(Array.isArray(byBusinessRes?.rows) ? byBusinessRes.rows : []);
      setBySalonRows(Array.isArray(bySalonRes?.rows) ? bySalonRes.rows : []);
      const nextFilters = summaryRes?.filters || DEFAULT_FILTERS;
      setFilters({
        ...DEFAULT_FILTERS,
        ...nextFilters,
        status_options: (nextFilters.status_options || []).length
          ? nextFilters.status_options
          : DEFAULT_FILTERS.status_options,
      });
    } catch (err) {
      setError(err?.message || "Unable to load maintenance analytics.");
      setSummary(null);
      setTrends([]);
      setTaskRows([]);
      setStaffRows([]);
      setByBusinessRows([]);
      setBySalonRows([]);
    } finally {
      setLoadingMain(false);
    }
  }, [appliedParams, bucket]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await getSuperAdminMaintenanceLogs({ ...appliedParams, page, pageSize });
      setLogsRows(Array.isArray(res?.rows) ? res.rows : []);
      setLogsTotal(Number(res?.total || 0));
    } catch (err) {
      setLogsRows([]);
      setLogsTotal(0);
    } finally {
      setLoadingLogs(false);
    }
  }, [appliedParams, page]);

  useEffect(() => {
    loadMain();
  }, [loadMain, refreshKey]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs, refreshKey]);

  const taskSort = useTableSort({
    rows: taskRows,
    columns: {
      task_name: { type: "text", getValue: (r) => r?.task_name },
      expected: { type: "number", getValue: (r) => r?.expected },
      completed: { type: "number", getValue: (r) => r?.completed },
      on_time_pct: { type: "number", getValue: (r) => r?.on_time_pct || 0 },
      overdue_count: { type: "number", getValue: (r) => r?.overdue_count },
      missed_count: { type: "number", getValue: (r) => r?.missed_count },
    },
    defaultSortBy: "overdue_count",
    fallbackKey: "task_name",
  });

  const staffSort = useTableSort({
    rows: staffRows,
    columns: {
      staff_name: { type: "text", getValue: (r) => r?.staff_name },
      expected: { type: "number", getValue: (r) => r?.expected },
      completed: { type: "number", getValue: (r) => r?.completed },
      on_time_pct: { type: "number", getValue: (r) => r?.on_time_pct || 0 },
      late_pct: { type: "number", getValue: (r) => r?.late_pct || 0 },
      missed_count: { type: "number", getValue: (r) => r?.missed_count },
    },
    defaultSortBy: "on_time_pct",
    fallbackKey: "staff_name",
  });

  const byBusinessSort = useTableSort({
    rows: byBusinessRows,
    columns: {
      business_name: { type: "text", getValue: (r) => r?.business_name },
      expected: { type: "number", getValue: (r) => r?.expected },
      completed: { type: "number", getValue: (r) => r?.completed },
      on_time_pct: { type: "number", getValue: (r) => r?.on_time_pct || 0 },
      overdue_count: { type: "number", getValue: (r) => r?.overdue_count },
      missed_count: { type: "number", getValue: (r) => r?.missed_count },
    },
    defaultSortBy: "expected",
    fallbackKey: "business_name",
  });

  const bySalonSort = useTableSort({
    rows: bySalonRows,
    columns: {
      salon_name: { type: "text", getValue: (r) => r?.salon_name },
      expected: { type: "number", getValue: (r) => r?.expected },
      completed: { type: "number", getValue: (r) => r?.completed },
      on_time_pct: { type: "number", getValue: (r) => r?.on_time_pct || 0 },
      overdue_count: { type: "number", getValue: (r) => r?.overdue_count },
      missed_count: { type: "number", getValue: (r) => r?.missed_count },
    },
    defaultSortBy: "expected",
    fallbackKey: "salon_name",
  });

  const trendMax = useMemo(() => {
    const values = trends.map((row) => Math.max(Number(row?.on_time_count || 0), Number(row?.late_count || 0), Number(row?.missed_count || 0)));
    return Math.max(1, ...values);
  }, [trends]);

  const logsHasPrev = page > 1;
  const logsHasNext = page * pageSize < logsTotal;
  const kpis = summary?.kpis ?? EMPTY_OBJECT;
  const hasAnyData = useMemo(
    () =>
      Number(kpis?.expected_occurrences || 0) > 0 ||
      Number(kpis?.completed_occurrences || 0) > 0 ||
      trends.length > 0 ||
      taskRows.length > 0 ||
      staffRows.length > 0 ||
      byBusinessRows.length > 0 ||
      bySalonRows.length > 0 ||
      logsTotal > 0,
    [byBusinessRows.length, bySalonRows.length, kpis, logsTotal, staffRows.length, taskRows.length, trends.length]
  );

  const exportLogsCsv = async () => {
    const rows = [];
    let currentPage = 1;
    const loopSize = 200;
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const res = await getSuperAdminMaintenanceLogs({ ...appliedParams, page: currentPage, pageSize: loopSize });
      const nextRows = Array.isArray(res?.rows) ? res.rows : [];
      rows.push(...nextRows);
      if (rows.length >= Number(res?.total || 0) || nextRows.length < loopSize) break;
      currentPage += 1;
    }
    const lines = [["Timestamp", "Business", "Salon", "Task", "Staff", "Status", "Hours Late", "Completed By", "Note"].join(",")];
    rows.forEach((row) => {
      lines.push([
        csvEscape(row.timestamp_local),
        csvEscape(scopeLabel(row.business_id, row.business_name)),
        csvEscape(scopeLabel(row.salon_id, row.salon_name)),
        csvEscape(row.task),
        csvEscape(row.staff_name || ""),
        csvEscape(row.status_label || ""),
        csvEscape(row.hours_late),
        csvEscape(row.completed_by_username || ""),
        csvEscape(row.note || ""),
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `superadmin_maintenance_logs_${appliedParams.startDate}_to_${appliedParams.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (!is_super_admin && !authLoading) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Maintenance & Cleaning Analytics" closeTo="/superadmin/reports" />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title={HELP_GUIDE.title}
              subtitle={HELP_GUIDE.subtitle}
              sections={HELP_GUIDE.sections}
              onJumpToSection={jumpToSectionWithHighlight}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">{error}</div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <label className={LABEL_TEXT}>Date From</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`${FIELD_CLASS} w-full`} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Date To</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`${FIELD_CLASS} w-full`} />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>Business Search</label>
                    <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.business} label="Business" />
                  </div>
                  <input type="search" value={businessSearch} onChange={(e) => setBusinessSearch(e.target.value)} placeholder="Search business" className={`${FIELD_CLASS} w-full`} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Business</label>
                  <select
                    value={businessId}
                    onChange={async (e) => {
                      const value = e.target.value;
                      setBusinessId(value);
                      setSalonId("");
                      setManualBusinessId(value);
                      await loadScopeFilters(value);
                    }}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={loadingFilters}
                  >
                    <option value="">All Businesses</option>
                    {filteredBusinesses.map((row) => (
                      <option key={row.id} value={row.id}>{scopeLabel(row.id, row.name)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={LABEL_TEXT}>Business ID (Manual)</label>
                  <div className="flex items-center gap-2">
                    <input type="text" value={manualBusinessId} onChange={(e) => setManualBusinessId(e.target.value)} placeholder="Paste Business ID" className={`${FIELD_CLASS} w-full`} />
                    <button
                      type="button"
                      onClick={async () => {
                        const nextId = String(manualBusinessId || "").trim();
                        if (!nextId) {
                          setBusinessId("");
                          setSalonId("");
                          await loadScopeFilters("");
                          return;
                        }
                        if (!/^\d+$/.test(nextId)) {
                          setError("Business ID must contain digits only.");
                          return;
                        }
                        setError("");
                        setBusinessId(nextId);
                        setSalonId("");
                        await loadScopeFilters(nextId);
                      }}
                      className={BTN_OUTLINE}
                    >
                      Use
                    </button>
                  </div>
                </div>

                <div>
                  <label className={LABEL_TEXT}>Salon Search</label>
                  <input
                    type="search"
                    value={salonSearch}
                    onChange={(e) => setSalonSearch(e.target.value)}
                    placeholder="Search salon"
                    className={`${FIELD_CLASS} w-full`}
                    disabled={!businessId}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>Salon</label>
                    <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.salon} label="Salon" />
                  </div>
                  <select value={salonId} onChange={(e) => setSalonId(e.target.value)} className={`${FIELD_CLASS} w-full`} disabled={!businessId}>
                    <option value="">All Salons</option>
                    {filteredSalons.map((row) => (
                      <option key={row.id} value={row.id}>{scopeLabel(row.id, row.name)}</option>
                    ))}
                  </select>
                  {filters.business_has_no_salons ? (
                    <div className="mt-1 text-xs text-amber-700">{filters.no_salons_note || "No salons configured for this business"}</div>
                  ) : null}
                </div>

                <div>
                  <label className={LABEL_TEXT}>Assigned Staff</label>
                  <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className={`${FIELD_CLASS} w-full`}>
                    <option value="">All</option>
                    {staffOptions.map((row) => (
                      <option key={row.id} value={row.id}>{scopeLabel(row.id, row.name)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Category</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${FIELD_CLASS} w-full`}>
                    <option value="">All</option>
                    {categoryOptions.map((row) => (
                      <option key={row} value={row}>{row}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Frequency Type</label>
                  <select value={frequencyType} onChange={(e) => setFrequencyType(e.target.value)} className={`${FIELD_CLASS} w-full`}>
                    <option value="">All</option>
                    {frequencyOptions.map((row) => (
                      <option key={row.value} value={row.value}>{row.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${FIELD_CLASS} w-full`}>
                    {statusOptions.map((row) => (
                      <option key={row.value} value={row.value}>{row.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Task</label>
                  <select value={taskId} onChange={(e) => setTaskId(e.target.value)} className={`${FIELD_CLASS} w-full`}>
                    <option value="">All</option>
                    {taskOptions.map((row) => (
                      <option key={row.id} value={row.id}>{row.title}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-8">
                  <input id="sa-maint-include-inactive" type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
                  <label htmlFor="sa-maint-include-inactive" className="text-sm text-slate-700">Include inactive tasks</label>
                </div>
              </div>

              {dateError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm">{dateError}</div>
              ) : null}
              {filtersError ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">{filtersError}</div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                  Timezone rule
                  <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.timezone_rule} label="Timezone rule" />
                </div>
                <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                  Business-only inclusion
                  <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.business_only_inclusion} label="Business-only inclusion" />
                </div>
                <div className="ml-auto flex flex-wrap gap-3">
                  <button type="button" onClick={runReport} className={`${BTN_PRIMARY} min-w-[140px]`} disabled={loadingMain}>{loadingMain ? "Loading..." : "Apply"}</button>
                  <button
                    type="button"
                    onClick={async () => {
                      setStartDate(defaultStart);
                      setEndDate(today);
                      setBusinessId("");
                      setSalonId("");
                      setStaffId("");
                      setCategory("");
                      setFrequencyType("");
                      setStatus("ALL");
                      setTaskId("");
                      setIncludeInactive(false);
                      setBusinessSearch("");
                      setSalonSearch("");
                      setManualBusinessId("");
                      setPage(1);
                      setAppliedParams({ startDate: defaultStart, endDate: today, status: "ALL", includeInactive: false });
                      setRefreshKey((prev) => prev + 1);
                      await loadScopeFilters("");
                    }}
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                  >
                    Reset
                  </button>
                </div>
              </div>

            {(summary?.meta?.timezone_fallback_business_ids || []).length ? (
                <div className="text-xs text-amber-700 inline-flex items-center gap-1.5">
                  Business timezone not set; using UTC.
                  <InfoTooltip
                    text={`Business timezone not set; using UTC. Affected business IDs: ${(summary?.meta?.timezone_fallback_business_ids || []).join(", ")}`}
                    label="UTC fallback"
                  />
                </div>
              ) : null}
            </div>

            {!loadingMain && !error && !hasAnyData ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                No maintenance data for the selected scope and date range.
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {[
                { label: "Total Completions", value: toNumberText(kpis.total_completions, 0) },
                { label: "Completion Rate", value: toPercentText(kpis.completion_rate_pct), tip: SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.completion_rate },
                { label: "Overdue Rate", value: toPercentText(kpis.overdue_rate_pct) },
                { label: "Avg Time Late", value: `${toNumberText(kpis.avg_time_late_hours, 2)}h` },
                { label: "Tasks At Risk", value: toNumberText(kpis.tasks_at_risk, 0) },
                { label: "Top Overdue Task", value: kpis?.most_overdue_task ? `${kpis.most_overdue_task.task_name} (${kpis.most_overdue_task.overdue_total})` : "--" },
                { label: "Top Performer Staff", value: kpis?.top_performer_staff ? `${kpis.top_performer_staff.staff_name} (${toPercentText(kpis.top_performer_staff.on_time_pct)})` : "--" },
                { label: "Consistency Score", value: toNumberText(kpis.consistency_score, 1) },
              ].map((card) => (
                <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                    {card.label}
                    {card.tip ? <InfoTooltip text={card.tip} label={card.label} /> : null}
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">{card.value}</div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
              <div className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
                Status Trends Over Time
                <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.section_trends} label="Status trends" />
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-56 flex items-end gap-2">
                  {trends.length ? (
                    trends.map((row, index) => {
                      const label = row?.label || "";
                      const onTime = Number(row?.on_time_count || 0);
                      const late = Number(row?.late_count || 0);
                      const missed = Number(row?.missed_count || 0);
                      const showLabel = trends.length <= 10 || index % Math.ceil(trends.length / 8) === 0;
                      return (
                        <div key={`${label}-${index}`} className="flex-1 min-w-0 flex flex-col items-center gap-1">
                          <div className="w-full h-40 flex items-end justify-center gap-0.5">
                            <div className="w-1/3 rounded-t bg-emerald-400" style={{ height: `${Math.max(2, (onTime / trendMax) * 100)}%` }} />
                            <div className="w-1/3 rounded-t bg-amber-400" style={{ height: `${Math.max(2, (late / trendMax) * 100)}%` }} />
                            <div className="w-1/3 rounded-t bg-rose-400" style={{ height: `${Math.max(2, (missed / trendMax) * 100)}%` }} />
                          </div>
                          <div className="text-[11px] text-slate-500 text-center min-h-[1rem]">{showLabel ? label : ""}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="w-full grid place-items-center text-slate-500 text-sm">{loadingMain ? "Loading..." : "No trend data in this range."}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900">Task Leaderboard</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <SortableTh label="Task" sortKey="task_name" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-left" />
                      <th className="px-3 py-2 text-left">Business</th>
                      <th className="px-3 py-2 text-left">Salon</th>
                      <SortableTh label="Expected" sortKey="expected" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" />
                      <th className="px-3 py-2 text-right">Completed</th>
                      <SortableTh label="On-time %" sortKey="on_time_pct" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" />
                      <SortableTh label="Overdue" sortKey="overdue_count" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" />
                      <SortableTh label="Missed" sortKey="missed_count" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {taskSort.sortedRows.map((row) => (
                      <tr key={`${row.task_id}-${row.salon_id || "none"}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-800">{row.task_name}</td>
                        <td className="px-3 py-2 text-slate-700">{scopeLabel(row.business_id, row.business_name)}</td>
                        <td className="px-3 py-2 text-slate-700">{scopeLabel(row.salon_id, row.salon_name)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.expected, 0)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.completed, 0)}</td>
                        <td className="px-3 py-2 text-right">{toPercentText(row.on_time_pct)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.overdue_count, 0)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.missed_count, 0)}</td>
                      </tr>
                    ))}
                    {!taskSort.sortedRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={8}>{loadingMain ? "Loading..." : "No task rows for this range."}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900">Staff Performance</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <SortableTh label="Staff" sortKey="staff_name" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-left" />
                      <th className="px-3 py-2 text-left">Business</th>
                      <th className="px-3 py-2 text-left">Salon</th>
                      <th className="px-3 py-2 text-right">Expected</th>
                      <th className="px-3 py-2 text-right">Completed</th>
                      <SortableTh label="On-time %" sortKey="on_time_pct" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" />
                      <SortableTh label="Late %" sortKey="late_pct" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" />
                      <SortableTh label="Missed" sortKey="missed_count" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {staffSort.sortedRows.map((row, idx) => (
                      <tr key={`${row.staff_id || "unassigned"}-${idx}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-800">{row.staff_name}</td>
                        <td className="px-3 py-2 text-slate-700">{scopeLabel(row.business_id, row.business_name)}</td>
                        <td className="px-3 py-2 text-slate-700">{scopeLabel(row.salon_id, row.salon_name)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.expected, 0)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.completed, 0)}</td>
                        <td className="px-3 py-2 text-right">{toPercentText(row.on_time_pct)}</td>
                        <td className="px-3 py-2 text-right">{toPercentText(row.late_pct)}</td>
                        <td className="px-3 py-2 text-right">{toNumberText(row.missed_count, 0)}</td>
                      </tr>
                    ))}
                    {!staffSort.sortedRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={8}>{loadingMain ? "Loading..." : "No staff rows for this range."}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900 inline-flex items-center gap-2">
                  By Business Leaderboard
                  <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.leaderboard_business} label="By business leaderboard" />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <SortableTh label="Business" sortKey="business_name" sortState={byBusinessSort.sortState} onSort={byBusinessSort.handleSort} className="px-3 py-2 text-left" />
                        <SortableTh label="Expected" sortKey="expected" sortState={byBusinessSort.sortState} onSort={byBusinessSort.handleSort} className="px-3 py-2 text-right" align="right" />
                        <SortableTh label="Completed" sortKey="completed" sortState={byBusinessSort.sortState} onSort={byBusinessSort.handleSort} className="px-3 py-2 text-right" align="right" />
                        <SortableTh label="On-time %" sortKey="on_time_pct" sortState={byBusinessSort.sortState} onSort={byBusinessSort.handleSort} className="px-3 py-2 text-right" align="right" />
                        <SortableTh label="Overdue" sortKey="overdue_count" sortState={byBusinessSort.sortState} onSort={byBusinessSort.handleSort} className="px-3 py-2 text-right" align="right" />
                        <SortableTh label="Missed" sortKey="missed_count" sortState={byBusinessSort.sortState} onSort={byBusinessSort.handleSort} className="px-3 py-2 text-right" align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {byBusinessSort.sortedRows.map((row) => (
                        <tr key={row.business_id} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-800">{scopeLabel(row.business_id, row.business_name)}</td>
                          <td className="px-3 py-2 text-right">{toNumberText(row.expected, 0)}</td>
                          <td className="px-3 py-2 text-right">{toNumberText(row.completed, 0)}</td>
                          <td className="px-3 py-2 text-right">{toPercentText(row.on_time_pct)}</td>
                          <td className="px-3 py-2 text-right">{toNumberText(row.overdue_count, 0)}</td>
                          <td className="px-3 py-2 text-right">{toNumberText(row.missed_count, 0)}</td>
                        </tr>
                      ))}
                      {!byBusinessSort.sortedRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={6}>{loadingMain ? "Loading..." : "No business rows for this range."}</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              </div>

              {!filters.business_has_no_salons ? (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900 inline-flex items-center gap-2">
                    By Salon Leaderboard
                    <InfoTooltip text={SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS.leaderboard_salon} label="By salon leaderboard" />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <SortableTh label="Salon" sortKey="salon_name" sortState={bySalonSort.sortState} onSort={bySalonSort.handleSort} className="px-3 py-2 text-left" />
                          <th className="px-3 py-2 text-left">Business</th>
                          <SortableTh label="Expected" sortKey="expected" sortState={bySalonSort.sortState} onSort={bySalonSort.handleSort} className="px-3 py-2 text-right" align="right" />
                          <SortableTh label="Completed" sortKey="completed" sortState={bySalonSort.sortState} onSort={bySalonSort.handleSort} className="px-3 py-2 text-right" align="right" />
                          <SortableTh label="On-time %" sortKey="on_time_pct" sortState={bySalonSort.sortState} onSort={bySalonSort.handleSort} className="px-3 py-2 text-right" align="right" />
                          <SortableTh label="Overdue" sortKey="overdue_count" sortState={bySalonSort.sortState} onSort={bySalonSort.handleSort} className="px-3 py-2 text-right" align="right" />
                          <SortableTh label="Missed" sortKey="missed_count" sortState={bySalonSort.sortState} onSort={bySalonSort.handleSort} className="px-3 py-2 text-right" align="right" />
                        </tr>
                      </thead>
                      <tbody>
                        {bySalonSort.sortedRows.map((row) => (
                          <tr key={row.salon_id} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-800">{scopeLabel(row.salon_id, row.salon_name)}</td>
                            <td className="px-3 py-2 text-slate-700">{scopeLabel(row.business_id, row.business_name)}</td>
                            <td className="px-3 py-2 text-right">{toNumberText(row.expected, 0)}</td>
                            <td className="px-3 py-2 text-right">{toNumberText(row.completed, 0)}</td>
                            <td className="px-3 py-2 text-right">{toPercentText(row.on_time_pct)}</td>
                            <td className="px-3 py-2 text-right">{toNumberText(row.overdue_count, 0)}</td>
                            <td className="px-3 py-2 text-right">{toNumberText(row.missed_count, 0)}</td>
                          </tr>
                        ))}
                        {!bySalonSort.sortedRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={7}>{loadingMain ? "Loading..." : "No salon rows for this range."}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                  {filters.no_salons_note || "No salons configured for this business"}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <div className="font-semibold text-slate-900">Audit Log</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setLogsOpen((prev) => !prev)} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">{logsOpen ? "Collapse" : "Expand"}</button>
                  <button type="button" onClick={exportLogsCsv} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" disabled={loadingLogs}>Export CSV</button>
                </div>
              </div>
              {logsOpen ? (
                <div className="space-y-3 p-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Timestamp</th>
                          <th className="px-3 py-2 text-left">Business</th>
                          <th className="px-3 py-2 text-left">Salon</th>
                          <th className="px-3 py-2 text-left">Task</th>
                          <th className="px-3 py-2 text-left">Staff</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Hours Late</th>
                          <th className="px-3 py-2 text-left">Completed By</th>
                          <th className="px-3 py-2 text-left">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logsRows.map((row) => (
                          <tr key={row.id} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-700">{row.timestamp_local}</td>
                            <td className="px-3 py-2 text-slate-700">{scopeLabel(row.business_id, row.business_name)}</td>
                            <td className="px-3 py-2 text-slate-700">{scopeLabel(row.salon_id, row.salon_name)}</td>
                            <td className="px-3 py-2 text-slate-800">{row.task}</td>
                            <td className="px-3 py-2 text-slate-700">{row.staff_name || "--"}</td>
                            <td className="px-3 py-2 text-slate-700">{row.status_label}</td>
                            <td className="px-3 py-2 text-slate-700">{toNumberText(row.hours_late, 2)}</td>
                            <td className="px-3 py-2 text-slate-700">{row.completed_by_username || "--"}</td>
                            <td className="px-3 py-2 text-slate-700">{row.note || "--"}</td>
                          </tr>
                        ))}
                        {!logsRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={9}>{loadingLogs ? "Loading logs..." : "No completion logs for this range."}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60" disabled={!logsHasPrev || loadingLogs}>Previous</button>
                    <span className="text-xs text-slate-500">Page {page}</span>
                    <button type="button" onClick={() => setPage((prev) => prev + 1)} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60" disabled={!logsHasNext || loadingLogs}>Next</button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
